import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Sparkles,
  ChevronDown,
  CheckCircle,
  HelpCircle,
  MessageSquare,
} from "lucide-react";
import { getContactContent, submitContactMessage, getLocalContactContent } from "../api";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import SEO from "../components/SEO";
import { buildFaqSchema, buildOrganizationSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const CATEGORIES = [
  "General Inquiry",
  "Listing Request / Add Course",
  "Data Correction / Update Hours",
  "Advertising & Partnership",
  "Feedback & Suggestions",
  "Other",
];

const ContactUs = () => {
  const { toast } = useToast();
  const [content, setContent] = useState(() => getLocalContactContent());
  const [openFaq, setOpenFaq] = useState(0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    category: "General Inquiry",
    subject: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    getContactContent()
      .then((data) => {
        if (data) setContent(data);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({
        title: "Missing Fields",
        description: "Please fill out all required fields before submitting.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      await submitContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        category: form.category,
        subject: form.subject.trim() || "Website Contact Form Submission",
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        category: "General Inquiry",
        subject: "",
        message: "",
      });
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We will get back to you shortly.",
      });
    } catch (err) {
      toast({
        title: "Error Sending Message",
        description: "Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = content?.faqs || [
    {
      q: "How can I add or update my mini golf course listing?",
      a: "You can reach out through our contact form with your venue name, address, website, and operating hours. Our editorial team will verify the details and publish your listing.",
    },
    {
      q: "Is it free to browse the directory?",
      a: "Yes, Mini Golf USA is 100% free for all players and visitors. You can search courses by state, city, rating, and features without any account or subscription.",
    },
    {
      q: "How often is course information updated?",
      a: "Our automated pipeline and community review team refresh listings weekly to ensure hours, pricing, and contact information remain accurate.",
    },
    {
      q: "Can I report incorrect hours or closed venues?",
      a: "Absolutely. If you spot outdated hours or a permanently closed venue, please send us a quick note via the contact form and we will update it immediately.",
    },
  ];

  const contactSchemas = [
    buildOrganizationSchema(),
    buildFaqSchema(faqs),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Contact Us", url: "/contact" },
    ]),
  ];

  return (
    <div className="bg-[#0d0e12] text-gray-100 min-h-screen font-sans" data-testid="contact-us-page">
      <SEO
        title="Contact Us | Mini Golf USA Directory"
        description="Get in touch with the Mini Golf USA team for course additions, data updates, partnerships, and questions."
        canonicalUrl="https://minigolfalabama.com/contact"
        ogType="website"
        schema={contactSchemas}
      />
      <Toaster />

      {/* ================= HERO SECTION ================= */}
      <section className="bg-[#111217] border-b border-gray-800 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-gray-600">/</span>
            <span className="text-gray-200 font-medium">Contact Us</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/15 backdrop-blur-md mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#e5243b]" />
              {content?.heroBadge || "GET IN TOUCH"}
            </div>

            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
              {content?.heroTitle || "We'd Love to Hear From You"}
            </h1>

            <p className="mt-4 text-base sm:text-lg text-gray-300 leading-relaxed font-normal">
              {content?.heroSubtitle ||
                "Have a question about a course, feedback on our directory, or want to list your venue? Our team is here to help."}
            </p>
          </div>
        </div>
      </section>

      {/* ================= CONTACT CARDS ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Email */}
          <div className="bg-[#181a22] border border-gray-800 rounded-2xl p-6 shadow-xl hover:border-gray-700 transition-all flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Email Us</div>
            <a
              href={`mailto:${content?.email || "contact@minigolfusa.online"}`}
              className="text-sm font-bold text-white hover:text-blue-400 transition-colors mt-1 truncate"
            >
              {content?.email || "contact@minigolfusa.online"}
            </a>
            <div className="text-xs text-gray-400 mt-2">Direct editorial & player support</div>
          </div>

          {/* Phone */}
          <div className="bg-[#181a22] border border-gray-800 rounded-2xl p-6 shadow-xl hover:border-gray-700 transition-all flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Call Us</div>
            <a
              href={`tel:${content?.phone || "(555) 382-4653"}`}
              className="text-sm font-bold text-white hover:text-emerald-400 transition-colors mt-1"
            >
              {content?.phone || "(555) 382-4653"}
            </a>
            <div className="text-xs text-gray-400 mt-2">Mon–Fri assistance line</div>
          </div>

          {/* Office Address */}
          <div className="bg-[#181a22] border border-gray-800 rounded-2xl p-6 shadow-xl hover:border-gray-700 transition-all flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 text-[#e5243b] flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Headquarters</div>
            <div className="text-sm font-bold text-white mt-1 leading-snug">
              {content?.address || "100 Fairway Blvd, Birmingham, AL 35203, USA"}
            </div>
            <div className="text-xs text-gray-400 mt-2">National Directory Operations</div>
          </div>

          {/* Business Hours */}
          <div className="bg-[#181a22] border border-gray-800 rounded-2xl p-6 shadow-xl hover:border-gray-700 transition-all flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center mb-4">
              <Clock className="w-6 h-6" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Operating Hours</div>
            <div className="text-sm font-bold text-white mt-1">
              {content?.hours || "Monday – Friday: 9:00 AM – 6:00 PM EST"}
            </div>
            <div className="text-xs text-emerald-400 font-semibold mt-2">
              ✓ {content?.responseTime || "Typical response within 24 hours"}
            </div>
          </div>
        </div>
      </section>

      {/* ================= FORM & FAQS SECTION ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-7 bg-[#181a22] border border-gray-800 rounded-3xl p-6 sm:p-10 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#e5243b]/10 text-[#e5243b] flex items-center justify-center">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Send Us a Message</h2>
                <p className="text-xs text-gray-400 mt-0.5">Fill out the form below and we will respond promptly.</p>
              </div>
            </div>

            {submitted ? (
              <div className="bg-emerald-950/40 border border-emerald-800/80 rounded-2xl p-8 text-center my-6">
                <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white">Thank You! Message Received</h3>
                <p className="text-sm text-gray-300 mt-2 max-w-md mx-auto">
                  We have received your message and our team will get back to you at <strong>{form.email}</strong> within 24 business hours.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-2">
                      Your Name <span className="text-[#e5243b]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. John Doe"
                      className="w-full px-4 py-3 text-sm bg-[#111217] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-2">
                      Your Email <span className="text-[#e5243b]">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="e.g. john@example.com"
                      className="w-full px-4 py-3 text-sm bg-[#111217] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-2">
                      Inquiry Category
                    </label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-4 py-3 text-sm bg-[#111217] border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
                    >
                      {CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="Brief topic summary..."
                      className="w-full px-4 py-3 text-sm bg-[#111217] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#e5243b]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-300 mb-2">
                    Your Message <span className="text-[#e5243b]">*</span>
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Provide details regarding your question, course listing request, or feedback..."
                    className="w-full px-4 py-3 text-sm bg-[#111217] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#e5243b] leading-relaxed"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#e5243b] hover:bg-[#cf1c31] text-white text-sm font-bold shadow-lg shadow-red-900/40 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  <span>{submitting ? "Sending..." : "Submit Message"}</span>
                </button>
              </form>
            )}
          </div>

          {/* FAQs Accordion */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#e5243b] mb-2">
                <HelpCircle className="w-4 h-4" />
                Quick Answers
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                {content?.faqTitle || "Frequently Asked Questions"}
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={idx}
                    className="bg-[#181a22] border border-gray-800 rounded-2xl overflow-hidden transition-all"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 text-sm font-bold text-white hover:text-[#e5243b] transition-colors"
                    >
                      <span>{faq.q}</span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-[#e5243b]" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4 pt-1 text-xs sm:text-sm text-gray-400 leading-relaxed border-t border-gray-800/60">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
